import type { MessageDescriptor } from "@lingui/core";

import { i18n } from "@lingui/core";
import { msg } from "@lingui/core/macro";
import { Trans } from "@lingui/react/macro";
import { ChevronRightIcon } from "lucide-react";
import { observer } from "mobx-react-lite";
import React, { Fragment, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import {
  apiGetClinicalStudies,
  apiGetClinicalStudiesStudyAcronymVisits,
  apiGetClinicalStudiesVisitUuidDataPreFill,
  ClinicalStudies,
  VisitAdminProcedure,
  VisitAvailableDiagnoses,
  VisitAvailableMedicalData,
  VisitAvailableSymptoms,
} from "../../../api";
import { AppModel } from "../../../app/app-model";
import { Button } from "../../../components/ui-kit/button";
import { resolveEnumLabel } from "../../../components/ui-kit/fields/utils";
import { Form } from "../../../components/ui-kit/form/form";
import { FormRow } from "../../../components/ui-kit/form/form-row";
import { FormTable } from "../../../components/ui-kit/form/form-table";
import { Spinner } from "../../../components/ui-kit/spinner";
import { RequiredDataType } from "../../../constants/enum-to-text";
import { assertStudyAcronym } from "../../../utils/clinical-studies";
import { useFetch } from "../../../utils/use-fetch";
import {
  assert,
  maybeCapitalizeFirstLetter,
  requireNonNull,
} from "../../../utils/utils";
import { AdminDataVisitField } from "./admin-data-visit-field";
import { Submittable } from "./base-data-visit-field";
import { DiagnosisDataVisitField } from "./diagnosis-data-visit-field";
import { MedicalDataVisitField } from "./medical-data-visit-field";
import { SymptomDataVisitField } from "./symptom-data-visit-field";
import { VisitComorbiditiesSection } from "./visit-comorbidities-section";
import { VisitMedicationsSection } from "./visit-medications-section";

export const VisitRoute = observer(function VisitRoute(props: {
  app: AppModel;
}) {
  const { study, visit: visitType } = useParams<{
    study: string;
    visit: string;
  }>();
  const studyAcronym = assertStudyAcronym(study);
  assert(visitType != null, "Visit route param is missing");
  const patient = props.app.requirePatient();
  const [visit, setVisit] = useState<any>();
  const submittablesRef = useRef<(Submittable | null)[]>([]);

  const { loading, refetch: refetchVisit } = useFetch(async () => {
    const visitData = await prepareVisitData({
      patientId: patient.patientId,
      study: studyAcronym,
      visitType: visitType,
    });
    setVisit(visitData);
  }, [patient, studyAcronym, visitType]);

  const navigate = useNavigate();

  const orderGroups = useMemo(
    () => (visit?.data_list ? groupByOrder(visit.data_list) : []),
    [visit],
  );

  const hasMedicalHistoryAdmin = useMemo(
    () =>
      visit?.data_list?.some(
        (item: any) =>
          item.data_definition.data_type === "admin" &&
          item.data_definition.procedure === "medical history",
      ) ?? false,
    [visit],
  );

  const hasConcomitantMedicationsAdmin = useMemo(
    () =>
      visit?.data_list?.some(
        (item: any) =>
          item.data_definition.data_type === "admin" &&
          item.data_definition.procedure === "concomitant medications",
      ) ?? false,
    [visit],
  );

  const itemIndices = useMemo(() => {
    const flat = orderGroups.flatMap((group) => {
      const subGroups =
        group.items.length > 1 ? getSubGroups(group.items) : null;
      return subGroups ? subGroups.flatMap((s) => s.items) : group.items;
    });

    return new Map(flat.map((item, i) => [item, i]));
  }, [orderGroups]);

  function renderItem(item: any, idx: number) {
    return (
      <FormRow
        key={idx}
        label={maybeCapitalizeFirstLetter(itemLabel(item))}
        hint={itemHint(item)}
        data={
          item.data_definition.data_type === "admin" ? (
            <AdminDataVisitField
              prefill={item as VisitAdminProcedure}
              patient={patient}
              ref={(h) => {
                submittablesRef.current[idx] = h;
              }}
              visitUuid={visit.study_visit_uuid}
              visitDatetime={visit.visit_datetime}
            />
          ) : item.data_definition.data_type === "medical-data" ? (
            <MedicalDataVisitField
              prefill={item as VisitAvailableMedicalData}
              patient={patient}
              ref={(h) => {
                submittablesRef.current[idx] = h;
              }}
              visitUuid={visit.study_visit_uuid}
            />
          ) : item.data_definition.data_type === "symptom" ? (
            <SymptomDataVisitField
              prefill={item as VisitAvailableSymptoms}
              patient={patient}
              ref={(h) => {
                submittablesRef.current[idx] = h;
              }}
              visitUuid={visit.study_visit_uuid}
            />
          ) : item.data_definition.data_type === "diagnosis" ? (
            <DiagnosisDataVisitField
              prefill={item as VisitAvailableDiagnoses}
              patient={patient}
              ref={(h) => {
                submittablesRef.current[idx] = h;
              }}
              visitUuid={visit.study_visit_uuid}
            />
          ) : null
        }
        mandatory={item.data_definition.required}
      />
    );
  }

  return loading ? (
    <Spinner />
  ) : (
    <>
      {hasMedicalHistoryAdmin && (
        <VisitComorbiditiesSection
          app={props.app}
          patient={patient}
          onSaved={refetchVisit}
        />
      )}

      {hasConcomitantMedicationsAdmin && (
        <VisitMedicationsSection
          app={props.app}
          patient={patient}
          onSaved={refetchVisit}
        />
      )}

      <Form
        onSubmit={async () => {
          await Promise.all(
            submittablesRef.current
              .filter((s) => s !== null)
              .map((s) => s.submit()),
          );
          await navigate("..");
        }}
      >
        <FormTable dataAdd>
          {orderGroups.map((group, groupIndex) => {
            const subGroups =
              group.items.length > 1 ? getSubGroups(group.items) : null;
            return (
              <Fragment key={group.order}>
                {groupIndex > 0 && <div className="col-span-full mt-4" />}
                {subGroups
                  ? subGroups.map((sub) => (
                      <CollapsibleGroup key={sub.label} label={sub.label}>
                        {sub.items.map((item) =>
                          renderItem(
                            item,
                            requireNonNull(itemIndices.get(item)),
                          ),
                        )}
                      </CollapsibleGroup>
                    ))
                  : group.items.map((item) =>
                      renderItem(item, requireNonNull(itemIndices.get(item))),
                    )}
              </Fragment>
            );
          })}
        </FormTable>

        <Button type="submit" className="mt-6">
          <Trans>Save</Trans>
        </Button>
      </Form>
    </>
  );
});

async function prepareVisitData({
  patientId,
  study,
  visitType,
}: {
  patientId: number;
  study: ClinicalStudies;
  visitType: string;
}) {
  const studiesResponse = await apiGetClinicalStudies({
    query: { patient_ids: [patientId] },
  });

  const enrollment = studiesResponse.data.study_enrollments.find(
    (s) => s.study_acronym === study,
  );

  assert(enrollment != null, "Enrollment not found");

  const visitsResponse = await apiGetClinicalStudiesStudyAcronymVisits({
    path: { study_acronym: study },
    query: { patient_ids: [patientId] },
  });

  const visit = visitsResponse.data.study_visits.find(
    (v) =>
      v.visit_type === visitType && v.study_enrollment_uuid === enrollment.uuid,
  );

  if (!visit) {
    return undefined;
  }

  const visitPrefillResponse = await apiGetClinicalStudiesVisitUuidDataPreFill({
    path: { visit_uuid: visit.uuid },
  });

  return visitPrefillResponse.data;
}

function CollapsibleGroup({
  label,
  children,
  defaultOpen = true,
}: {
  label: string;
  children: React.ReactNode;
  defaultOpen?: boolean;
}) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <>
      <div
        className="border-border col-span-full mb-2 mt-3 flex cursor-pointer select-none items-center gap-1 text-muted-foreground border-b pb-1 text-sm font-medium"
        onClick={() => setOpen(!open)}
      >
        {label}
        <ChevronRightIcon
          className={`size-4 transition-transform ${open ? "rotate-90" : ""}`}
        />
      </div>
      {open && children}
    </>
  );
}

function groupByOrder(dataList: any[]): OrderGroup[] {
  const sorted = [...dataList].sort(
    (a: any, b: any) => a.data_definition.order - b.data_definition.order,
  );
  const groups: OrderGroup[] = [];
  for (const item of sorted) {
    const last = groups[groups.length - 1];
    if (last?.order === item.data_definition.order) {
      last.items.push(item);
    } else {
      groups.push({ order: item.data_definition.order, items: [item] });
    }
  }
  return groups;
}

function getSubGroups(items: any[]): SubGroup[] {
  const map = new Map<string, SubGroup>();
  for (const item of items) {
    const dt = item.data_definition.data_type;
    const key =
      dt === "medical-data"
        ? `medical:${item.data_definition.category ?? ""}`
        : dt;
    if (!map.has(key)) {
      const catLabel = MedicalCategoryLabel[item.data_definition.category];
      const label =
        dt === "medical-data"
          ? catLabel
            ? i18n._(catLabel)
            : (item.data_definition.category ?? i18n._(msg`Medical data`))
          : maybeCapitalizeFirstLetter(
              resolveEnumLabel(RequiredDataType[dt]) || dt,
            );
      map.set(key, { label, items: [] });
    }
    requireNonNull(map.get(key)).items.push(item);
  }
  return [...map.values()];
}

function itemLabel(item: any): string | null {
  const def = item.data_definition;
  switch (def.data_type) {
    case "admin":
      return def.procedure;
    case "medical-data":
    case "symptom":
    case "diagnosis":
    case "computed":
      return def.data_name;
    case "ppg":
      return def.measurement_condition;
    default:
      return null;
  }
}

function itemHint(item: any): string | null {
  switch (item.data_definition.data_type) {
    case "admin":
      return i18n._(msg`Administrative procedure`);
    case "medical-data":
      return i18n._(msg`Medical`);
    case "symptom":
      return i18n._(msg`Symptom`);
    case "diagnosis":
      return i18n._(msg`Diagnosis`);
    case "ppg":
      return i18n._(msg`PPG`);
    case "computed":
      return i18n._(msg`Computed`);
    case "derived":
      return i18n._(msg`Derived`);
    default:
      return null;
  }
}

interface OrderGroup {
  order: number;
  items: any[];
}

interface SubGroup {
  label: string;
  items: any[];
}

const MedicalCategoryLabel: Record<string, MessageDescriptor> = {
  basic: msg`Basic`,
  vitals: msg`Vitals`,
  labs: msg`Labs`,
  exams_basic: msg`Basic exams`,
  exams_echo: msg`Echo exams`,
  exams_rhc: msg`RHC exams`,
  exams_lhc: msg`LHC exams`,
  exams_final: msg`Final exams`,
};
